/**
 * Topup Flow Models
 *
 * Covers the full "Recharger portefeuille" flow:
 *   Amount entry → Method selection → Token selection → Auth → Category/Note → Submit
 *
 * API endpoints:
 *   POST /api/topup_withdrawal_method      → TopupMethodsResponse
 *   POST /api/token                        → TopupTokenResponse
 *   POST /api/merchant/gozem_money/auth_txn → TopupAuthTxnResponse
 *   POST /api/gozem_money/merchant/init_psp_topup → InitTopupResponse
 */

// ── Method (Processor / Agent) ─────────────────────────────────────────────

export interface TopupRequiredParam {
  name: string;
  display_name: string;
  /**
   * Field input type from the API.
   *  'integer' — numeric/phone field (render PhoneInputComponent when name === 'phone')
   *  'string'  — free-text input
   *  'list'    — dropdown; options are in the `values` array
   */
  type: 'integer' | 'string' | 'list';
  /** Max character length for 'integer' and 'string' types */
  limit?: string;
  /**
   * Whether the field is optional.
   * The API is inconsistent — may be a boolean false or the string "false" / "true".
   * Always normalise with: param.optional === true || param.optional === 'true'
   */
  optional: boolean | string;
  /**
   * Selectable options — only present when type === 'list'.
   * Each entry has a machine key and a human-readable value.
   */
  values?: { key: string; value: string }[];
  /**
   * When 'true' or true, this field's submitted value becomes the token's
   * display_key shown in the saved-account list.
   */
  display_key?: string | boolean;
}

export interface TopupProcessor {
  _id: string;
  processor_name: string;
  processor_display_name: string;
  processor_type: number;
  /** Determines token_type when creating a new token */
  payment_mode: number;
  logo_url: string;
  config: unknown | null;
  top_up_required_params: TopupRequiredParam[];
  withdrawal_required_params: TopupRequiredParam[];
  add_token_text: string;
  select_token_text: string;
}

export interface TopupMethod {
  id: string;
  name: string;
  /**
   * Relative image path served from S3.
   * Construct full URL as: ConfigService.s3BaseUrl() + method.icon
   * e.g. "method_images/img_Mixx_03190e17-a937-4b3b-8835-719df7098e0b.jpg"
   */
  icon: string;
  /** Transaction fee in local currency */
  fee: number;
  method_type: 'Mobile Agent' | 'Processor';
  processor: TopupProcessor | null;
  operation_avg_duration: string;
  is_gozem_money_processor: boolean;
  token: string | null;
}

// ── Token (saved payment account) ─────────────────────────────────────────

export interface TopupToken {
  _id: string;
  /** Processor or brand logo URL — may be empty */
  logo_url: string;
  processor_id: string;
  /** 1 = TOP_UP, 2 = WITHDRAW */
  token_service: number;
  /** Equals processor.payment_mode */
  token_type: number;
  status: boolean;
  /** Display string — phone number or masked card number */
  display_key: string;
  /** Arbitrary key-value properties (phone, name, int_phone for mobile) */
  token_properties: Record<string, unknown>;
  token_name: string;
}

// ── Categories ─────────────────────────────────────────────────────────────

export interface TopupCategory {
  id: string;
  name_en: string;
  name_fr: string;
  color: string;
  /** Full S3 icon URL */
  icon: string;
}

// ── Auth Transaction ───────────────────────────────────────────────────────

export interface TopupFee {
  creditor: number;
  debtor: number;
}

export interface TopupAuthTxnResult {
  amount: number;
  currency: string;
  fee: TopupFee;
  commission: TopupFee;
  default_category_id: string;
  default_category: TopupCategory;
  categories: TopupCategory[];
  type: string;
  is_pin_required: boolean;
}

// ── Dialog step ────────────────────────────────────────────────────────────

export type TopupDialogStep = 'form' | 'summary' | 'pin' | 'success';

// ── API Requests / Responses ───────────────────────────────────────────────

/** Common auth credentials included in every Topup API request */
interface TopupBaseRequest {
  merchant_id: string;
  account_id: string;
  merchant_user_id: string;
  user_type: 'merchant';
  /** Primary GozemMoney wallet account ID — sourced from wallet_accounts section (in-memory only, never localStorage) */
  user_gm_account_id: string;
}

/** POST /api/topup_withdrawal_method */
export interface TopupMethodsRequest extends TopupBaseRequest {
  /** Amount entered by the user — used by the backend to filter eligible methods */
  amount: number;
  operation_type: string;
}

export interface TopupMethodsResponse {
  success: boolean;
  methods: TopupMethod[];
}

/** POST /api/token */
export interface TopupTokenRequest extends TopupBaseRequest {
  /** Processor ID from the selected TopupMethod */
  processor_id: string;
  /** 1 = TOP_UP */
  token_service: 1;
}

export interface TopupTokenResponse {
  success: boolean;
  tokens: TopupToken[];
  error_code?: string;
  message?: string;
}

/** POST /api/merchant/gozem_money/auth_txn */
export interface TopupAuthTxnRequest extends TopupBaseRequest {
  amount: number;
  processor_id: string;
  token_id: string;
  /** Always "Topup" for this endpoint */
  txn_type: string;
  /** ID of the selected TopupMethod — returned by /api/topup_withdrawal_method as method.id */
  transaction_method_id: string;
}

export interface TopupAuthTxnResponse {
  success: boolean;
  authorize_trxn: TopupAuthTxnResult;
}

/** POST /api/gozem_money/merchant/init_psp_topup */
export interface InitTopupRequest extends TopupBaseRequest {
  amount: number;
  /** User-selected category ID — omitted when no category chosen */
  category?: string;
  processor_id: string;
  token_id: string;
  /** Always "Topup" for this endpoint */
  txn_type: string;
  shared_note: string;
  fee: TopupFee;
  commission: TopupFee;
  payment_tags: unknown[];
  payment_references: unknown[];
  /** Only sent when auth_txn.is_pin_required = true */
  pin?: string;
}

/**
 * Transaction status codes returned by the backend in `transaction_status`.
 *
 *   NEW          = 0  — transaction created, not yet processed
 *   PROCESSING   = 1  — actively being processed by the PSP (also returned as 4)
 *   PENDING      = 2  — awaiting async callback (typical immediate state after init)
 *   COMPLETED    = 3  — definitively confirmed by the PSP
 *   PENDING_ADMIN= 6  — flagged for manual review
 *   CANCELLED    = 9  — cancelled by user or system
 */
export enum TransactionStatus {
  NEW = 0,
  PROCESSING = 1,
  PENDING = 2,
  COMPLETED = 3,
  PENDING_ADMIN = 6,
  CANCELLED = 9,
}

/** Parsed status from the transaction detail API, used on success screens. */
export interface PostTxStatus {
  statusValue: number;
  statusLabel: string;
}

/**
 * Maps a backend `transaction_status` value to a UI tone.
 * Handles the alternate PROCESSING serialisation (1 and 4).
 */
export function txStatusTone(
  statusValue: number,
): 'success' | 'warning' | 'info' | 'danger' | 'neutral' {
  switch (statusValue) {
    case TransactionStatus.COMPLETED:
      return 'success';
    case TransactionStatus.PENDING:
      return 'warning';
    case TransactionStatus.PROCESSING:
    case 4: // alternate PROCESSING serialisation
      return 'info';
    case TransactionStatus.CANCELLED:
      return 'danger';
    default:
      return 'neutral';
  }
}

/**
 * Maps a PostTxStatus to the variant accepted by GzStatusPage.
 * Returns 'success' while the detail is still loading (status === null).
 */
export function txStatusPageVariant(
  status: PostTxStatus | null,
): 'loading' | 'success' | 'error' | 'info' {
  if (status === null) return 'loading';
  switch (status.statusValue) {
    case TransactionStatus.COMPLETED:
      return 'success';
    case TransactionStatus.CANCELLED:
      return 'error';
    default:
      return 'info'; // PENDING, PROCESSING, NEW, PENDING_ADMIN
  }
}

/** POST /api/gozem_money/merchant/init_psp_topup - success response */
export interface InitTopupResponse {
  success: boolean;
  response_code: string;
  transaction_id: string;
  payment_id: string;
  /** See TransactionStatus enum. PENDING (2) is the typical immediate-success value. */
  transaction_status: TransactionStatus;
  /** Short numeric reference — preferred for display over transaction_id */
  transaction_unique_id?: number;
  /** Updated wallet balance after topup */
  wallet: number;
  top_up_ussd: string;
  added_wallet_amount: number;
  callback: boolean;
}
